import React, { useEffect, useState, useRef } from 'react';
import {
    Box,
    Card,
    CardContent,
    CardHeader,
    Checkbox,
    FormControlLabel, Table, TableBody, TableCell,
    TableContainer,
    TableHead, TableRow,
    TextField,
    IconButton,
    Typography,
    Menu,
    MenuItem,
} from "@mui/material";
import { DatePicker, DesktopDatePicker } from "@mui/x-date-pickers";
import { AdapterDateFns } from "@mui/x-date-pickers/AdapterDateFns";
import moment from "moment/moment";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import FlightService from '../../services/FlightService';
import CachedIcon from '@mui/icons-material/Cached';
import MoreVertOutlinedIcon from '@mui/icons-material/MoreVertOutlined';


const cssInJs = {
    header: {
        backgroundColor: "#fff",
    },
    grayBlock: {
        background: '#fafafa',
        padding: '10px',
    },
    textInput: {
        marginRight: '20px',
        marginBottom: '15px'
    },
    formControl: {
        minWidth: 80,
        marginRight: '20px',
    },
    flexContainer: {
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
    },
}

function FlightControlLogs({ onJobLogClick, onLoadedJobLogData, fetchData }) {
    const [jobLogs, setJobLogs] = useState([]);
    const [loading, setLoading] = useState(true);
    const [filter, setFilter] = useState({
        startDate: null,
        endDate: null,
        showErrorsOnly: false,
    });
    const [open_more, setOpenMore] = useState(false)
    const more_element = useRef()

    useEffect(() => {
        const fetchData = async () => {
            try {
                const jobLogResponse = await FlightService.getFlightJobLogs();
                const filteredJobLogs = jobLogResponse.filter((job) => {
                    const isDateInRange =
                        (!filter.startDate || moment(job.start_date).isSameOrAfter(filter.startDate)) &&
                        (!filter.endDate || moment(job.end_date).isSameOrBefore(filter.endDate));

                    const isError = job.result === 'Error';

                    return (!filter.showErrorsOnly || (filter.showErrorsOnly && isError)) && isDateInRange;
                });

                setJobLogs(filteredJobLogs);
                onLoadedJobLogData(filteredJobLogs);
            } catch (error) {
                console.error('Error', error);
            } finally {
                setLoading(false);
            }
        };

        fetchData();
    }, [filter, onLoadedJobLogData]);

    const handleTableClick = (job) => {
        onJobLogClick(job);
    };

    const handleCheckboxChange = () => {
        setFilter((prevFilter) => ({
            ...prevFilter,
            showErrorsOnly: !prevFilter.showErrorsOnly,
        }));
    };

    const handleDateChange = (date, field) => {
        setFilter((prevFilter) => ({
            ...prevFilter,
            [field]: date,
        }));

        // Check if both start and end dates are null, reset filter
        if (field === 'startDate' && !date && !filter.endDate) {
            setFilter((prevFilter) => ({
                ...prevFilter,
                startDate: null,
                endDate: null,
            }));
        } else if (field === 'endDate' && !date && !filter.startDate) {
            setFilter((prevFilter) => ({
                ...prevFilter,
                startDate: null,
                endDate: null,
            }));
        }
    };

    const handleReloadData = async () => {
        try {
            setLoading(true);
            const jobLogResponse = await FlightService.getFlightJobLogs();
                const filteredJobLogs = jobLogResponse.filter((job) => {
                    const isDateInRange =
                        (!filter.startDate || moment(job.start_date).isSameOrAfter(filter.startDate)) &&
                        (!filter.endDate || moment(job.end_date).isSameOrBefore(filter.endDate));

                    const isError = job.result === 'Error';

                    return (!filter.showErrorsOnly || (filter.showErrorsOnly && isError)) && isDateInRange;
                });

                setJobLogs(filteredJobLogs);
                onLoadedJobLogData(filteredJobLogs);
        } catch (error) {
            console.error('Error while reloading data:', error);
        } finally {
            setLoading(false);
            setOpenMore(false); // Закрываем меню после нажатия на кнопку
        }
    };


    return (
        <Card>
            <div style={{ display: 'flex', alignItems: 'center' }}>
                <CardHeader
                    title={'Flight Control Logs'}
                    sx={{
                        ...cssInJs.header,
                        '& .MuiTypography-root': {
                            fontSize: '16px',
                        },
                        flexGrow: 1,
                    }}
                />
                <IconButton onClick={e => { setOpenMore(true) }} ref={more_element}>
                    <MoreVertOutlinedIcon sx={{ color: 'black' }} />
                </IconButton>
                <Menu
                    open={open_more}
                    anchorEl={more_element.current}
                    onClose={e => { setOpenMore(false) }}
                >
                    <MenuItem dense onClick={handleReloadData}>Reload Logs</MenuItem>
                </Menu>
            </div>
            <Box sx={cssInJs.grayBlock}>
                <div style={cssInJs.flexContainer}>
                    <LocalizationProvider dateAdapter={AdapterDateFns}>
                        <DesktopDatePicker
                            label="Start date"
                            value={filter.startDate}
                            inputFormat="MMM dd"
                            mask=""
                            onChange={(e) => handleDateChange(moment(e), 'startDate')}
                            renderInput={(params) => (
                                <TextField
                                    {...params}
                                    fullWidth={true}
                                    variant="outlined"
                                    size="small"
                                    sx={cssInJs.textInput}
                                />
                            )}
                        />
                    </LocalizationProvider>

                    <LocalizationProvider dateAdapter={AdapterDateFns}>
                        <DesktopDatePicker
                            label="End date"
                            value={filter.endDate}
                            inputFormat="MMM dd"
                            mask=""
                            onChange={(e) => handleDateChange(moment(e), 'endDate')}
                            renderInput={(params) => (
                                <TextField
                                    {...params}
                                    fullWidth={true}
                                    variant="outlined"
                                    size="small"
                                    sx={cssInJs.textInput}
                                />
                            )}
                        />
                    </LocalizationProvider>

                    {/* Checkbox Component */}
                    <FormControlLabel
                        control={<Checkbox checked={filter.showErrorsOnly}
                            onChange={handleCheckboxChange}
                            color="primary" />}
                        label="Error"
                    />
                </div>
            </Box>
            <CardContent>
                {loading ? (
                    <p>Loading...</p>
                ) : (
                    <TableContainer>
                        <Table>
                            <TableHead>
                                <TableRow>
                                    <TableCell>Code</TableCell>
                                    <TableCell>Name</TableCell>
                                    <TableCell>Period</TableCell>
                                    <TableCell>Start</TableCell>
                                    <TableCell>End</TableCell>
                                    <TableCell>Result</TableCell>
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {jobLogs.length > 0 ? (
                                    jobLogs.map((job) => (
                                        <TableRow key={job.id} onClick={() => handleTableClick(job)}>
                                            <TableCell>{job.client_id}</TableCell>
                                            <TableCell>{job.client.Name}</TableCell>
                                            <TableCell>{job.period}</TableCell>
                                            <TableCell>{job.start_date}</TableCell>
                                            <TableCell>{job.end_date}</TableCell>
                                            <TableCell>{job.result ?? 'No result'}</TableCell>
                                        </TableRow>
                                    ))
                                ) : (
                                    <TableRow>
                                        <TableCell align="left" colSpan={6}>
                                            No available data
                                        </TableCell>
                                    </TableRow>
                                )}
                            </TableBody>
                        </Table>
                    </TableContainer>
                )}
            </CardContent>
        </Card>
    );
}

export default FlightControlLogs